// Resolves Storyblok story links (linktype: "story") to correct internal hrefs.
// Handles SB locale prefix stripping, translated slugs via story.full_slug,
// and /lp/ prefix normalization via getCanonicalStoryLink.
// No "server-only" — works in both server and client components.

import { getSbLocaleFromUri } from "../locale-helper"
import { getCanonicalStoryLink, getLinkWithPrefixFromURLAndLocale } from "./link-utils"

/**
 * Resolves a Storyblok multilink story href from cached_url + optional resolved story data.
 *
 * Storyblok's cached_url has two known issues (GitHub #729, #751):
 * 1. It includes an SB locale prefix for non-default languages (e.g., "/fr/lp/projekte")
 *    but the site uses subdomains for locales, not URL prefixes.
 * 2. It always contains the default (German) slug, not the translated slug.
 *
 * When resolve_links: "story" is used, the SDK merges the resolved story into link.story,
 * which contains the correctly translated full_slug (e.g., "fr/lp/projects").
 *
 * @param cachedUrl - The cached_url from the Storyblok multilink field
 * @param sbLocale - The current SB locale (e.g., "fr", "en", "de-ch")
 * @param story - The resolved story object from resolve_links (optional)
 */
export function resolveStoryblokStoryHref(
  cachedUrl: string,
  sbLocale?: string,
  story?: { full_slug?: string }
): string {
  // 1. Prefer story.full_slug (has the translated slug from resolve_links)
  let slug = story?.full_slug ?? cachedUrl
  let isLp = false;

  // 2. Normalize: ensure no leading slash for consistent processing
  if (slug.startsWith("/")) {
    slug = slug.substring(1)
  }

  // 3. Strip SB locale prefix (e.g., "fr/lp/projects" → "lp/projects")
  const sbLocaleFromSlug = getSbLocaleFromUri("/" + slug)

  if (sbLocaleFromSlug) {
    slug = slug.substring(sbLocaleFromSlug.length + 1) // +1 for the "/"
  }

  // 4. Strip lp/ prefix — getCanonicalStoryLink will re-add it properly
  if (slug.startsWith("lp/")) {
    slug = slug.substring(3)
    isLp = true;
  }

  // 5. Generate canonical URL with /lp/ prefix + locale URI prefix (/fr/CHF/ etc.)
  //    Prefer explicitly provided sbLocale, else fall back to the locale detected from the slug.
  const effectiveLocale = sbLocale ?? sbLocaleFromSlug
  return getCanonicalStoryLink(slug, effectiveLocale, isLp)
}
